import { supabase } from '../lib/supabase';

// ── EGW Reading Plans ─────────────────────────────────────────────────────────

export const egwPlanService = {
  // Fetch all plans (admin)
  async getAllPlans() {
    const { data, error } = await supabase?.from('egw_reading_plans')?.select('*')?.order('created_at', { ascending: false });
    return { data, error };
  },

  // Fetch published/active plans (user-facing)
  async getPublishedPlans() {
    const { data, error } = await supabase?.from('egw_reading_plans')?.select('*')?.eq('is_active', true)?.order('created_at', { ascending: false });
    return { data, error };
  },

  // Create a new plan
  async createPlan(planData, userId) {
    const id = crypto.randomUUID();
    const { id: _ignored, ...rest } = planData;
    const { data, error } = await supabase?.from('egw_reading_plans')?.insert({ id, ...rest, created_by: userId })?.select()?.single();
    return { data, error };
  },

  // Update a plan
  async updatePlan(planId, updates) {
    const { data, error } = await supabase?.from('egw_reading_plans')?.update({ ...updates, updated_at: new Date()?.toISOString() })?.eq('id', planId)?.select()?.single();
    return { data, error };
  },

  // Delete a plan
  async deletePlan(planId) {
    const { error } = await supabase?.from('egw_reading_plans')?.delete()?.eq('id', planId);
    return { error };
  },

  // Toggle published status
  async togglePublished(planId, isPublished) {
    return egwPlanService?.updatePlan(planId, { is_published: isPublished });
  },

  // Fetch distinct book codes from egw_content
  async getAvailableBookCodes() {
    const { data, error } = await supabase?.from('egw_content')?.select('book_code')?.order('book_code', { ascending: true });
    if (error) return { data: [], error };
    const unique = [...new Set((data || []).map(r => r.book_code))];
    return { data: unique, error: null };
  },

  // Fetch all distinct pages for a book from egw_content
  async getBookPages(bookCode) {
    // Supabase has a default 1000-row limit per request.
    // egw_content has multiple paragraphs per page, so we must paginate
    // to collect ALL rows before grouping by page_number.
    const PAGE_SIZE = 1000;
    let allRows = [];
    let from = 0;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await supabase
        ?.from('egw_content')
        ?.select('page_number, content, content_sequence_id')
        ?.eq('book_code', bookCode)
        ?.order('content_sequence_id', { ascending: true })
        ?.range(from, from + PAGE_SIZE - 1);

      if (error) return { data: [], error };

      allRows = allRows?.concat(data || []);

      if (!data || data?.length < PAGE_SIZE) {
        hasMore = false;
      } else {
        from += PAGE_SIZE;
      }
    }

    // Group by page_number, keep first paragraph per page
    const pageMap = {};
    for (const row of allRows) {
      if (!pageMap?.[row?.page_number]) {
        pageMap[row.page_number] = {
          page_number: row?.page_number,
          preview: (row?.content || '')?.substring(0, 60),
          content_sequence_id: row?.content_sequence_id,
        };
      }
    }
    const pages = Object.values(pageMap)?.sort((a, b) => a?.page_number - b?.page_number);
    return { data: pages, error: null };
  },

  // Fetch days for a plan
  async getPlanDays(planId) {
    const { data, error } = await supabase?.from('egw_plan_days')?.select('*, egw_plan_day_pages(*)')?.eq('plan_id', planId)?.order('day_number', { ascending: true });
    return { data, error };
  },

  // Auto-generate days from pace
  async autoGenerateDays(planId, bookCode, pace) {
    // Fetch all pages for the book (paginated to bypass 1000-row limit)
    const { data: pages, error: pagesError } = await egwPlanService?.getBookPages(bookCode);
    if (pagesError) return { error: pagesError };

    // Delete existing days (cascades to egw_plan_day_pages via FK)
    const { error: deleteError } = await supabase?.from('egw_plan_days')?.delete()?.eq('plan_id', planId);
    if (deleteError) return { error: deleteError };

    // Group pages into days
    const dayGroups = [];
    for (let i = 0; i < pages?.length; i += pace) {
      dayGroups?.push(pages?.slice(i, i + pace));
    }

    // Batch-insert all days at once
    const dayInserts = dayGroups?.map((_, idx) => ({
      plan_id: planId,
      day_number: idx + 1,
    }));

    const { data: insertedDays, error: daysError } = await supabase
      ?.from('egw_plan_days')
      ?.insert(dayInserts)
      ?.select('id, day_number');
    if (daysError) return { error: daysError };

    // Sort inserted days by day_number to align with dayGroups
    const sortedDays = (insertedDays || [])?.sort((a, b) => a?.day_number - b?.day_number);

    // Build all page inserts in one array
    const allPageInserts = [];
    for (let dayIdx = 0; dayIdx < sortedDays?.length; dayIdx++) {
      const dayRow = sortedDays?.[dayIdx];
      const pagesForDay = dayGroups?.[dayIdx] || [];
      for (let order = 0; order < pagesForDay?.length; order++) {
        const pg = pagesForDay?.[order];
        allPageInserts?.push({
          day_id: dayRow?.id,
          plan_id: planId,
          page_number: pg?.page_number,
          paragraph_start: pg?.content_sequence_id,
          display_order: order,
        });
      }
    }

    // Batch-insert pages in chunks of 500 to avoid request size limits
    const CHUNK_SIZE = 500;
    for (let i = 0; i < allPageInserts?.length; i += CHUNK_SIZE) {
      const chunk = allPageInserts?.slice(i, i + CHUNK_SIZE);
      const { error: pageError } = await supabase?.from('egw_plan_day_pages')?.insert(chunk);
      if (pageError) return { error: pageError };
    }

    // Update plan totals
    await supabase?.from('egw_reading_plans')?.update({
      total_days: dayGroups?.length,
      total_pages: pages?.length,
      updated_at: new Date()?.toISOString(),
    })?.eq('id', planId);

    return { error: null, totalDays: dayGroups?.length, totalPages: pages?.length };
  },

  // Add a new empty day
  async addDay(planId, dayNumber) {
    const { data, error } = await supabase?.from('egw_plan_days')?.insert({ plan_id: planId, day_number: dayNumber })?.select()?.single();
    return { data, error };
  },

  // Remove an empty day
  async removeDay(dayId) {
    const { error } = await supabase?.from('egw_plan_days')?.delete()?.eq('id', dayId);
    return { error };
  },

  // Update day title
  async updateDayTitle(dayId, title) {
    const { error } = await supabase?.from('egw_plan_days')?.update({ title })?.eq('id', dayId);
    return { error };
  },

  // Assign a page to a day
  async assignPageToDay(dayId, planId, pageNumber, paragraphStart, displayOrder) {
    const { data, error } = await supabase?.from('egw_plan_day_pages')?.insert({ day_id: dayId, plan_id: planId, page_number: pageNumber, paragraph_start: paragraphStart, display_order: displayOrder })?.select()?.single();
    return { data, error };
  },

  // Remove a page from a day
  async removePageFromDay(pageId) {
    const { error } = await supabase?.from('egw_plan_day_pages')?.delete()?.eq('id', pageId);
    return { error };
  },

  // Move page to different day
  async movePageToDay(pageId, newDayId, newPlanId, displayOrder) {
    const { error } = await supabase?.from('egw_plan_day_pages')?.update({ day_id: newDayId, plan_id: newPlanId, display_order: displayOrder })?.eq('id', pageId);
    return { error };
  },
};

// ── EGW User Enrollment ───────────────────────────────────────────────────────

export const egwEnrollmentService = {
  async getUserEnrollments(userId) {
    const { data, error } = await supabase?.from('egw_user_enrollments')?.select('*, egw_reading_plans(*)')?.eq('user_id', userId)?.eq('is_active', true);
    return { data, error };
  },

  async enrollUser(userId, planId, pace) {
    const { data, error } = await supabase?.from('egw_user_enrollments')?.upsert({
        user_id: userId,
        plan_id: planId,
        pace_pages_per_day: pace || 3,
        is_active: true,
        is_complete: false,
      }, { onConflict: 'user_id,plan_id' })?.select()?.single();
    return { data, error };
  },

  async updatePace(enrollmentId, pace) {
    const { error } = await supabase?.from('egw_user_enrollments')?.update({ pace_pages_per_day: pace })?.eq('id', enrollmentId);
    return { error };
  },

  async markDayComplete(userId, planId, dayNumber, enrollmentId) {
    // Insert completion record
    const { error: compError } = await supabase?.from('egw_user_day_completions')?.upsert({ user_id: userId, plan_id: planId, day_number: dayNumber }, { onConflict: 'user_id,plan_id,day_number' });
    if (compError) return { error: compError };

    // Advance current_day
    const { error: advError } = await supabase?.from('egw_user_enrollments')?.update({ current_day: dayNumber + 1, last_read_at: new Date()?.toISOString() })?.eq('id', enrollmentId);
    return { error: advError };
  },

  async getCompletedDays(userId, planId) {
    const { data, error } = await supabase?.from('egw_user_day_completions')?.select('day_number')?.eq('user_id', userId)?.eq('plan_id', planId);
    return { data: (data || [])?.map(r => r?.day_number), error };
  },
};

// ── EGW Audio ─────────────────────────────────────────────────────────────────

export const egwAudioService = {
  async getAudioFile(bookCode, sequenceNumber, voice) {
    const { data, error } = await supabase?.from('egw_audio_files')?.select('*')?.eq('book_code', bookCode)?.eq('sequence_number', sequenceNumber)?.eq('voice', voice)?.maybeSingle();
    return { data, error };
  },

  async getSignedUrl(mp3Path) {
    const { data, error } = await supabase?.storage?.from('egw-audio')?.createSignedUrl(mp3Path, 3600);
    if (error || !data) return null;
    return data?.signedUrl;
  },

  async getPageMarks(audioFileId) {
    const { data, error } = await supabase?.from('egw_audio_page_marks')?.select('*')?.eq('audio_file_id', audioFileId)?.order('time_seconds', { ascending: true });
    return { data: data || [], error };
  },

  async getAvailableVoices(bookCode, sequenceNumber) {
    const { data, error } = await supabase?.from('egw_audio_files')?.select('voice')?.eq('book_code', bookCode)?.eq('sequence_number', sequenceNumber);
    if (error || !data) return [];
    return [...new Set(data.map(r => r.voice))];
  },
};

// ── EGW Content Reader ────────────────────────────────────────────────────────

export const egwContentService = {
  async getDayContent(planId, dayNumber, bookCode) {
    // Get day record — use maybeSingle() to avoid PGRST116 error when no row found
    const { data: dayData, error: dayError } = await supabase
      ?.from('egw_plan_days')
      ?.select('id, title')
      ?.eq('plan_id', planId)
      ?.eq('day_number', dayNumber)
      ?.maybeSingle();

    if (dayError) return { data: null, error: dayError };
    if (!dayData) return { data: null, error: new Error(`Day ${dayNumber} not found for plan ${planId}`) };

    // Get pages for this day
    const { data: dayPages, error: pagesError } = await supabase
      ?.from('egw_plan_day_pages')
      ?.select('page_number, display_order')
      ?.eq('day_id', dayData?.id)
      ?.order('display_order', { ascending: true });

    if (pagesError) return { data: null, error: pagesError };

    if (!dayPages?.length) return { data: { day: dayData, pages: [], paragraphs: [] }, error: null };

    const pageNumbers = dayPages?.map(p => p?.page_number);

    // Fetch content for these pages, filtered by book_code if provided
    let query = supabase
      ?.from('egw_content')
      ?.select('content_sequence_id, book_code, page_number, paragraph_number, content')
      ?.in('page_number', pageNumbers)
      ?.order('content_sequence_id', { ascending: true });

    if (bookCode) {
      query = query?.eq('book_code', bookCode);
    }

    const { data: paragraphs, error: contentError } = await query;

    return {
      data: {
        day: dayData,
        pages: dayPages,
        paragraphs: paragraphs || [],
      },
      error: contentError,
    };
  },
};
